/* eslint max-lines-per-function: ["error", {max: 320}] -- this hook is complex*/
/* eslint max-statements: ["error", {max: 50}] -- this hook is complex*/
/* eslint complexity: ["error", {max: 50}] -- this hook is complex*/
/* eslint max-lines: ["error", {max: 350}] -- this hook is complex*/

import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";

import usePatientStatus from "@/hooks/usePatientStatus";
import useAppMonitor from "@/services/AppMonitor";
import {
  useGetSchedulePermissionsQuery,
  useLazyGetPatientAppointmentTypesQuery,
} from "@/store/api/schedule/booking";
import { useGetFullProfileQuery } from "@/store/api/user";
import { useAppSelector } from "@/store/hooks";
import {
  selectAppUserExternalId,
  selectAppUserIsPatient,
  selectAppUserLinkedPatients,
} from "@/store/reducers/appUser";
import {
  getPatientIdsToProcess,
  getTreatmentTypeFromProgramName,
  parseAppointmentPermissions,
  shouldSkipProcessing,
} from "@/utils/schedulePermissions.util";

const useSchedulePermissions = (): UseSchedulePermissionsState => {
  const { captureException } = useAppMonitor();
  const externalId = useAppSelector(selectAppUserExternalId);
  const isPatient = useAppSelector(selectAppUserIsPatient);
  const linkedPatients = useAppSelector(selectAppUserLinkedPatients);
  const allPatients = linkedPatients;
  const {
    data: fullProfileData,
    isFetching: isFullProfileFetching,
    isLoading: isFullProfileLoading,
  } = useGetFullProfileQuery();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [
    patientIdsWithBookingPermissions,
    setPatientIdsWithBookingPermissions,
  ] = useState<string[]>([]);
  const [isBookingPermissionsFetched, setIsBookingPermissionsFetched] =
    useState<boolean>(false);
  const [schedulePermissions, setSchedulePermissions] = useState<
    Record<string, SchedulePermissionsMapper>
  >({});
  const [patientAppointmentTypesMapper, setPatientAppointmentTypesMapper] =
    useState<Record<string, ProviderAppointmentTypes[]>>({});

  // Refs to stabilize function references for useEffect dependencies
  const processSchedulePermissionsRef =
    useRef<(res: SchedulePermissionResponse) => void>(null);
  const processPatientAppointmentTypesRef =
    useRef<
      (args: { patientIds: string[]; shouldForce?: boolean }) => Promise<void>
    >(null);
  const lastProcessedPatientIdsRef = useRef<string>("");

  const {
    hasLinkedPatientInTreatment,
    isCurrentOrSupportLinkedUserInTreatment,
    isCurrentUserInTreatment,
  } = usePatientStatus(fullProfileData);

  const {
    data: schedulePermissionsData,
    isLoading: isSchedulePermissionsLoading,
    refetch: refetchSchedulePermissionsQuery,
  } = useGetSchedulePermissionsQuery(undefined, {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    skip: !isCurrentOrSupportLinkedUserInTreatment,
  });

  const [getAppointmentTypes] = useLazyGetPatientAppointmentTypesQuery();

  const processSchedulePermissions = useCallback(
    (res: SchedulePermissionResponse) => {
      // More specific guard clause - only skip if we have no valid user data
      if (
        isEmpty(externalId) ||
        (isPatient && isEmpty(fullProfileData?.programName)) ||
        (!isPatient && isEmpty(allPatients))
      ) {
        return;
      }

      const permissionsByPatient: Record<string, SchedulePermissionsMapper> =
        {};

      const targetPatients = isPatient
        ? [
            {
              externalId,
              isProxy: false,
              programName: fullProfileData?.programName,
            },
          ]
        : allPatients;

      targetPatients?.forEach((patient) => {
        const { externalId: patientExternalId, isProxy, programName } = patient;
        const treatmentType = getTreatmentTypeFromProgramName(programName);

        if (!treatmentType) return;

        permissionsByPatient[patientExternalId] = parseAppointmentPermissions({
          isProxy: isProxy ?? false,
          isSupport: !isPatient,
          permissionsDefinitions: res.data,
          treatmentType,
        });
      });

      setSchedulePermissions(permissionsByPatient);
    },
    [externalId, isPatient, fullProfileData, allPatients],
  );

  const updatePatientIdsWithBookingPermissions = useCallback(
    (appointmentTypesMapper: Record<string, ProviderAppointmentTypes[]>) => {
      const patientsWithAppointmentTypes = Object.keys(
        appointmentTypesMapper,
      ).filter((patientId) => {
        return appointmentTypesMapper[patientId].length > 0;
      });

      // For support users, also add their own ID if they have bookable patients
      const allUsersWithBookingPermissions = [...patientsWithAppointmentTypes];
      if (!isPatient && !isEmpty(patientsWithAppointmentTypes) && externalId) {
        allUsersWithBookingPermissions.push(externalId);
      }

      setIsBookingPermissionsFetched(true);
      setPatientIdsWithBookingPermissions(allUsersWithBookingPermissions);
      setIsLoading(false);
    },
    [externalId, isPatient],
  );

  const processPatientAppointmentTypes = useCallback(
    async ({
      patientIds,
      shouldForce = false,
    }: {
      patientIds: string[];
      shouldForce?: boolean;
    }) => {
      // Prevent concurrent executions of this async callback unless forced (e.g., PTR)
      if (isLoading && !shouldForce) {
        return;
      }
      setIsLoading(true);

      try {
        // Fetch all patients' appointment types concurrently
        const results = await Promise.allSettled(
          patientIds.map(async (patientId) => {
            const response = await getAppointmentTypes({
              patientExternalId: patientId,
            }).unwrap();
            return { patientId, response };
          }),
        );

        // Build new mapper from scratch to avoid stale state
        const newMapper: Record<string, ProviderAppointmentTypes[]> = {};
        for (const result of results) {
          // eslint-disable-next-line max-depth
          if (result.status === "fulfilled") {
            newMapper[result.value.patientId] = result.value.response;
          } else {
            captureException(result.reason);
          }
        }

        // Update state atomically
        setPatientAppointmentTypesMapper(newMapper);

        // Call update function with the fresh data
        updatePatientIdsWithBookingPermissions(newMapper);
      } catch (error) {
        captureException(error);
        setIsLoading(false);
      }
    },
    [
      getAppointmentTypes,
      updatePatientIdsWithBookingPermissions,
      captureException,
      isLoading,
    ],
  );

  // Update function refs to latest versions
  useEffect(() => {
    processSchedulePermissionsRef.current = processSchedulePermissions;
  }, [processSchedulePermissions]);

  useEffect(() => {
    processPatientAppointmentTypesRef.current = processPatientAppointmentTypes;
  }, [processPatientAppointmentTypes]);

  useEffect(() => {
    if (isNil(schedulePermissionsData) || isEmpty(schedulePermissionsData.data))
      return;

    // Wait for fullProfileData to be loaded for patient users
    if (isPatient && isFullProfileLoading) {
      return;
    }

    // Also re-process when linked patients list changes (important for support users)
    processSchedulePermissionsRef.current?.(schedulePermissionsData);
  }, [schedulePermissionsData, isPatient, isFullProfileLoading, allPatients]);

  useEffect(() => {
    const isSupportWithProfileInfoLoading = !isPatient && isFullProfileLoading;

    // Guard against empty externalId or loading states
    if (!externalId || isSupportWithProfileInfoLoading) {
      return;
    }

    if (
      shouldSkipProcessing({
        hasLinkedPatientInTreatment,
        isCurrentUserInTreatment,
        isPatient,
      })
    ) {
      return;
    }

    const patientIds = getPatientIdsToProcess({
      allPatients,
      externalId,
      isPatient,
    });

    // Create a string representation of the patient IDs for comparison
    const patientIdsString = JSON.stringify(
      // eslint-disable-next-line max-params
      [...patientIds].sort((a, b) => a.localeCompare(b)),
    );

    if (patientIds.length > 0) {
      // Check if we've already processed these same patient IDs recently
      if (lastProcessedPatientIdsRef.current === patientIdsString) {
        return;
      }

      lastProcessedPatientIdsRef.current = patientIdsString;
      // Use the ref to get the latest function
      void processPatientAppointmentTypesRef.current?.({ patientIds });
    }
  }, [
    externalId,
    isPatient,
    allPatients,
    isFullProfileLoading,
    isCurrentUserInTreatment,
    hasLinkedPatientInTreatment,
  ]);

  const refetchSchedulePermissions = useCallback(async () => {
    if (!isCurrentOrSupportLinkedUserInTreatment) return;

    lastProcessedPatientIdsRef.current = "";
    await refetchSchedulePermissionsQuery();

    // Clearing lastProcessedPatientIdsRef alone won't re-run the appointment-types
    // useEffect when its state dependencies haven't changed. Directly trigger
    // re-processing so patientIdsWithBookingPermissions is fully refreshed.
    if (
      shouldSkipProcessing({
        hasLinkedPatientInTreatment,
        isCurrentUserInTreatment,
        isPatient,
      })
    ) {
      return;
    }
    const patientIds = getPatientIdsToProcess({
      allPatients,
      externalId,
      isPatient,
    });
    const processAppointmentTypes = processPatientAppointmentTypesRef.current;
    if (patientIds.length > 0 && processAppointmentTypes) {
      await processAppointmentTypes({ patientIds, shouldForce: true });
    }
  }, [
    externalId,
    isPatient,
    allPatients,
    hasLinkedPatientInTreatment,
    isCurrentOrSupportLinkedUserInTreatment,
    isCurrentUserInTreatment,
    refetchSchedulePermissionsQuery,
  ]);

  return useMemo(
    () => ({
      appointmentTypesForPatient: (patientId: string) => {
        const result = patientAppointmentTypesMapper[patientId] ?? [];
        return result;
      },
      isBookingPermissionsFetched,
      isLoading:
        isLoading ||
        isSchedulePermissionsLoading ||
        isFullProfileLoading ||
        isFullProfileFetching,
      patientAppointmentTypesMapper,
      patientIdsWithBookingPermissions,
      refetchSchedulePermissions,
      schedulePermissionsByPatientMapper: schedulePermissions,
    }),
    [
      patientAppointmentTypesMapper,
      isBookingPermissionsFetched,
      isLoading,
      isSchedulePermissionsLoading,
      isFullProfileLoading,
      isFullProfileFetching,
      patientIdsWithBookingPermissions,
      refetchSchedulePermissions,
      schedulePermissions,
    ],
  );
};

export default useSchedulePermissions;
